import { useCallback, useMemo, useState } from 'react'
import { DEALS_COL, DealClientCell, DealPropertyCell, DealSortHead, formatDealDate, formatMonthlyPrice, formatMonthlyPriceWithPeriod } from '@/components/deals-table'
import { platformLogo } from '@/lib/platforms'
import { CreateDealDialog } from '@/components/create-deal-dialog'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import {
	DndContext,
	DragOverlay,
	MouseSensor,
	TouchSensor,
	pointerWithin,
	rectIntersection,
	useDroppable,
	useSensor,
	useSensors,
	type DragStartEvent,
	type DragEndEvent,
	type UniqueIdentifier,
} from '@dnd-kit/core'
import {
	SortableContext,
	useSortable,
	verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import {
	Badge,
	Button,
	cn,
	DealKanbanCard,
	DealStageElement,
	SearchInput,
	MessageCounter,
	Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
	Spinner,
	Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
	Tabs, TabsList, TabsTrigger,
	DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
	IconButton,
} from '@rentflowdev/ui'
import { Building2, EllipsisVerticalIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useListDealsQuery, useListMeetingsQuery, useListPropertiesQuery, useUpdateDealStageMutation, useUpdateDealStatusMutation } from '@/api'
import { formatPhone } from '@/lib/phone'
import { disambiguatePropertyLabels } from '@/lib/property-options'
import { StartRentalDialog } from '@/components/start-rental-dialog'
import { hideDeal, selectDealStageOverrides, selectHiddenDealIds, setDealStage } from '@/store/deal-stage-overrides.slice'
import { ACTIVE_STAGES, DEAL_STAGE_KEYS, DEFAULT_LOCALE } from '@/types'
import type { Deal, DealFilters, DealStage } from '@/types'
import { useCan } from '@/lib/access'

/** UI-augmented Deal — adds frontend-only display fields the backend Deal does not yet expose. */
type EnrichedDeal = Deal & {
	propertyTitle?: string
	propertyAddress?: string
	propertyImage?: string
	propertyPrice?: string
	clientName?: string
	clientCompany?: string
	clientPhone?: string
	clientEmail?: string
	badgeLabel?: string
	badgeVariant?: 'default' | 'info' | 'warning' | 'success'
	messageCount?: number
}


export const Route = createFileRoute('/_app/deals/active')({
	component: DealsActivePage,
})

// ── Helpers ──────────────────────────────────────────────

const STAGE_VARIANT: Record<DealStage, 'new' | 'contact' | 'negotiation' | 'meeting' | 'contract'> = {
	NEW: 'new',
	CONTACT: 'contact',
	NEGOTIATION: 'negotiation',
	VIEWING: 'meeting',
	CONTRACT: 'contract',
	ACTIVE: 'contract',
	CLOSED: 'contract',
	CANCELLED: 'new',
}

const DEAL_STAGE_BADGE_VARIANT: Record<DealStage, 'secondary' | 'info' | 'warning' | 'success' | 'destructive'> = {
	NEW:         'secondary',
	CONTACT:     'info',
	NEGOTIATION: 'info',
	VIEWING:     'warning',
	CONTRACT:    'success',
	ACTIVE:      'success',
	CLOSED:      'success',
	CANCELLED:   'destructive',
}

function formatDate(iso: string) {
	return new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })
}

/**
 * Ближайшая встреча одной строкой: «Завтра, 14:00 — Показ».
 *
 * Сегодня и завтра называются словом, дальше — датой: на доске важно не
 * «когда именно», а «скоро ли», и слово читается быстрее числа.
 */
function formatNextEvent(iso: string, t: (key: string) => string): string {
	const when = new Date(iso)
	const today = new Date()
	const midnight = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()
	const days = Math.round((midnight(when) - midnight(today)) / 86_400_000)
	const day = days === 0
		? t('deals.card.today')
		: days === 1
			? t('deals.card.tomorrow')
			: when.toLocaleDateString(DEFAULT_LOCALE, { day: 'numeric', month: 'short' })
	const time = when.toLocaleTimeString(DEFAULT_LOCALE, { hour: '2-digit', minute: '2-digit' })
	return `${day}, ${time} — ${t('deals.card.viewing')}`
}

/** «Анна Кузнецова» → «АК». Две буквы — всё, что помещается в кружок. */
function avatarInitials(name: string): string {
	return name.trim().split(/\s+/).slice(0, 2).map((w) => w[0]?.toUpperCase() ?? '').join('')
}

function renderBadge(deal: EnrichedDeal) {
	if (deal.badgeLabel) {
		return <Badge variant={deal.badgeVariant ?? 'default'} size="md">{deal.badgeLabel}</Badge>
	}
	if (deal.source) {
		const logo = platformLogo(deal.source)
		return logo
			? <Badge variant="logo" size="md" logoSrc={logo} logoAlt={deal.source} />
			: <Badge variant="secondary" size="md">{deal.source}</Badge>
	}
	// Источника может не быть, и прочерк в бейдже сообщает об этом громче, чем
	// стоит: верхняя строка тогда несёт только дату.
	return null
}

function applyFilters(deals: EnrichedDeal[], filters: DealFilters): EnrichedDeal[] {
	return deals
		.filter((d) => !filters.property || d.propertyId === filters.property)
		.filter((d) => !filters.client || d.contactId === filters.client)
		.filter((d) => !filters.source || d.source === filters.source)
		.filter((d) => {
			if (!filters.search) return true
			const q = filters.search.toLowerCase()
			return (d.propertyTitle?.toLowerCase().includes(q))
				|| (d.clientName?.toLowerCase().includes(q))
				|| (d.clientCompany?.toLowerCase().includes(q))
				|| d.id.toLowerCase().includes(q)
		})
}

// ── DnD Components ──────────────────────────────────────

function SortableDealCard({ deal, nextEvent }: { deal: EnrichedDeal; nextEvent?: string }) {
	const navigate = useNavigate()
	const { t } = useTranslation()
	const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
		useSortable({ id: deal.id, data: { stage: deal.stage } })

	// Каждая ссылка внутри карточки гасит всплытие: карточка целиком ведёт в
	// сделку, а адрес и имя — в объект и в профиль клиента. Без этого щелчок по
	// ссылке срабатывал бы дважды и уводил не туда.
	const go = (e: React.MouseEvent, to: () => void) => {
		e.stopPropagation()
		to()
	}

	return (
		<DealKanbanCard
			ref={setNodeRef}
			style={{
				transform: CSS.Transform.toString(transform),
				transition,
				opacity: isDragging ? 0.4 : 1,
			}}
			{...attributes}
			{...listeners}
			onClick={() => navigate({ to: '/deals/$dealId', params: { dealId: deal.id } })}
			source={renderBadge(deal)}
			date={formatDate(deal.createdAt)}
			messages={deal.messageCount
				? (
					<button
						type="button"
						aria-label={t('deals.card.openChat')}
						onClick={(e) => go(e, () => void navigate({ to: '/deals/$dealId', params: { dealId: deal.id } }))}
					>
						<MessageCounter count={deal.messageCount} />
					</button>
				)
				: undefined}
			address={deal.propertyId
				? (
					<button
						type="button"
						className="text-left hover:underline"
						onClick={(e) => go(e, () => void navigate({ to: '/properties/$propertyId', params: { propertyId: deal.propertyId! } }))}
					>
						{deal.propertyTitle ?? deal.title ?? `Сделка №${deal.id.slice(-6)}`}
					</button>
				)
				: (deal.propertyTitle ?? deal.title ?? `Сделка №${deal.id.slice(-6)}`)}
			price={formatMonthlyPriceWithPeriod(deal)}
			clientName={deal.clientName
				? (
					<button
						type="button"
						className="truncate text-left hover:underline"
						onClick={(e) => go(e, () => void navigate({ to: '/tenants/$tenantId', params: { tenantId: deal.contactId ?? '' } }))}
					>
						{deal.clientName}
					</button>
				)
				: undefined}
			clientPhone={formatPhone(deal.clientPhone) || undefined}
			nextEvent={nextEvent
				? (
					<Badge variant="warning" size="sm" className="truncate">{nextEvent}</Badge>
				)
				: undefined}
			manager={renderManager(deal)}
		/>
	)
}

/** Только аватар: колонка узкая, а имя ответственного её съедало. */
function renderManager(deal: EnrichedDeal) {
	if (!deal.manager) return null
	return deal.manager.avatarUrl
		? <img src={deal.manager.avatarUrl} alt={deal.manager.name} title={deal.manager.name} className="size-6 rounded-full object-cover" />
		: (
			<span
				title={deal.manager.name}
				className="flex size-6 items-center justify-center rounded-full bg-(--color-muted) text-[10px] font-[500] text-(--color-muted-foreground)"
			>
				{avatarInitials(deal.manager.name)}
			</span>
		)
}

function KanbanColumn({ stage, label, count, deals, nextEvents }: {
	stage: DealStage; label: string; count: number; deals: EnrichedDeal[]; nextEvents: Map<string, string>
}) {
	const { setNodeRef, isOver } = useDroppable({
		id: `column-${stage}`,
		data: { type: 'column', stage },
	})
	const dealIds = useMemo(() => deals.map((d) => d.id), [deals])

	return (
		<div
			ref={setNodeRef}
			className={cn(
				'flex min-w-0 flex-1 flex-col gap-3 rounded-lg p-0.5 transition-colors',
				isOver && 'bg-(--color-muted)/50',
			)}
		>
			<DealStageElement type={STAGE_VARIANT[stage]} label={label} count={count} />
			<SortableContext items={dealIds} strategy={verticalListSortingStrategy}>
				<div className="flex min-h-20 flex-1 flex-col gap-2">
					{deals.map((deal) => <SortableDealCard key={deal.id} deal={deal} nextEvent={nextEvents.get(deal.id)} />)}
				</div>
			</SortableContext>
		</div>
	)
}

function DragActionZone({ id, label, variant }: {
	id: string; label: string; variant: 'success' | 'destructive'
}) {
	const { setNodeRef, isOver } = useDroppable({ id })
	return (
		<div
			ref={setNodeRef}
			className={cn(
				// h-32, not larger: taller zones overlap the bottom band of the rightmost
				// kanban columns on laptop viewports, and pointerWithin turns drops there
				// into unconfirmed cancel/close actions.
				'flex h-32 w-60 items-center justify-center overflow-hidden transition-all',
				variant === 'success'
					? 'bg-(--color-success-light) text-(--color-success)'
					: 'bg-(--color-destructive-light) text-(--color-destructive)',
				isOver && 'ring-2 ring-inset',
				isOver && variant === 'success' && 'ring-(--color-success)',
				isOver && variant === 'destructive' && 'ring-(--color-destructive)',
			)}
		>
			<span className="text-base font-medium leading-6">{label}</span>
		</div>
	)
}

// ── List View ───────────────────────────────────────────

type ListSortField = 'property' | 'client' | 'price' | 'stage' | 'date'

function DealsListView({ deals }: { deals: EnrichedDeal[] }) {
	const { t } = useTranslation()
	const navigate = useNavigate()
	const [updateDealStatus] = useUpdateDealStatusMutation()
	const [sortField, setSortField] = useState<ListSortField>('date')
	const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc')

	function toggleSort(field: ListSortField) {
		if (sortField === field) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
		else { setSortField(field); setSortDir('asc') }
	}

	const sorted = useMemo(() => [...deals].sort((a, b) => {
		const dir = sortDir === 'asc' ? 1 : -1
		switch (sortField) {
			case 'property': return (a.propertyTitle ?? '').localeCompare(b.propertyTitle ?? '') * dir
			case 'client':   return (a.clientName ?? '').localeCompare(b.clientName ?? '') * dir
			case 'stage':    return a.stage.localeCompare(b.stage) * dir
			case 'price':    return (Number(a.propertyPrice ?? 0) - Number(b.propertyPrice ?? 0)) * dir
			case 'date':     return (new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()) * dir
		}
	}), [deals, sortField, sortDir])

	return (
		<div className="flex min-h-0 flex-1 flex-col overflow-auto px-4 pb-4">
			<div className="overflow-hidden rounded-lg border border-(--color-border)">
				<Table className="table-fixed">
					<TableHeader>
						<TableRow className="hover:bg-transparent">
							<DealSortHead field="property" active={sortField === 'property'} dir={sortDir} onToggle={toggleSort}>
								{t('deals.table.property')}
							</DealSortHead>
							<DealSortHead field="client" className={DEALS_COL.client} active={sortField === 'client'} dir={sortDir} onToggle={toggleSort}>
								{t('deals.table.client')}
							</DealSortHead>
							<DealSortHead field="price" className={DEALS_COL.price} active={sortField === 'price'} dir={sortDir} onToggle={toggleSort}>
								{t('deals.table.price')}
							</DealSortHead>
							<TableHead className={`${DEALS_COL.source} px-4 py-3 font-[500] text-(--color-muted-foreground)`}>
								{t('deals.table.source')}
							</TableHead>
							<DealSortHead field="stage" className={DEALS_COL.status} active={sortField === 'stage'} dir={sortDir} onToggle={toggleSort}>
								{t('deals.table.stage')}
							</DealSortHead>
							<DealSortHead field="date" className={DEALS_COL.date} active={sortField === 'date'} dir={sortDir} onToggle={toggleSort}>
								{t('deals.table.date')}
							</DealSortHead>
							<TableHead className={DEALS_COL.action} />
						</TableRow>
					</TableHeader>
					<TableBody>
						{sorted.map((deal) => (
							<TableRow
								key={deal.id}
								className="h-24 cursor-pointer"
								onClick={() => navigate({ to: '/deals/$dealId', params: { dealId: deal.id } })}
							>
								<DealPropertyCell
									image={deal.propertyImage}
									title={deal.propertyTitle ?? `Сделка №${deal.id.slice(-6)}`}
									address={deal.propertyAddress}
									onOpenProperty={deal.propertyId
										? () => void navigate({ to: '/properties/$propertyId', params: { propertyId: deal.propertyId! } })
										: undefined}
								/>
								<DealClientCell
									name={deal.clientName}
									phone={deal.clientPhone}
									email={deal.clientEmail}
									fallback={deal.contactId}
								/>
								<TableCell className={`${DEALS_COL.price} px-4 text-sm text-(--color-foreground)`}>
									{formatMonthlyPrice(deal) ?? '—'}
								</TableCell>
								<TableCell className={`${DEALS_COL.source} px-4`}>
									{deal.source && (platformLogo(deal.source)
										? <Badge variant="logo" size="sm" logoSrc={platformLogo(deal.source)} logoAlt={deal.source} />
										: <Badge variant="secondary" size="sm">{deal.source}</Badge>
									)}
								</TableCell>
								<TableCell className={`${DEALS_COL.status} px-4`}>
									<Badge variant={DEAL_STAGE_BADGE_VARIANT[deal.stage]} size="sm">
										{t(DEAL_STAGE_KEYS[deal.stage])}
									</Badge>
								</TableCell>
								<TableCell className={`${DEALS_COL.date} px-4 text-sm text-(--color-muted-foreground)`}>
									{formatDealDate(deal.createdAt)}
								</TableCell>
								<TableCell className={`${DEALS_COL.action} px-2`} onClick={(e) => e.stopPropagation()}>
									<DropdownMenu>
										<DropdownMenuTrigger render={<IconButton variant="ghost" size="sm" aria-label={t('common.actions')} />}>
											<EllipsisVerticalIcon className="size-4" />
										</DropdownMenuTrigger>
										<DropdownMenuContent align="end" className="w-auto min-w-48">
											{deal.propertyId && (
												<DropdownMenuItem
													onClick={() => void navigate({ to: '/properties/$propertyId', params: { propertyId: deal.propertyId! } })}
												>
													<Building2 className="size-4" />
													{t('deals.table.openProperty')}
												</DropdownMenuItem>
											)}
											{/* Зеркало «Вернуть в работу» на соседней вкладке: отсюда
											    сделка уходит в завершённые, оттуда возвращается. */}
											<DropdownMenuItem onClick={() => void updateDealStatus({ id: deal.id, body: { status: 'COMPLETED' } })}>
												{t('deals.table.markRented')}
											</DropdownMenuItem>
											<DropdownMenuItem onClick={() => void updateDealStatus({ id: deal.id, body: { status: 'CANCELLED' } })}>
												{t('deals.table.markRejected')}
											</DropdownMenuItem>
										</DropdownMenuContent>
									</DropdownMenu>
								</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			</div>
		</div>
	)
}

// ── Page ─────────────────────────────────────────────────

const DEFAULT_FILTERS: DealFilters = { property: '', client: '', source: '', search: '' }

function DealsActivePage() {
	const { t } = useTranslation()
	const canCreate = useCan('create', 'Deal')
	// Closing a deal starts its rental.
	const canStartRental = useCan('create', 'Rental')
	const dispatch = useDispatch()
	const storeOverrides = useSelector(selectDealStageOverrides)
	const storeHidden    = useSelector(selectHiddenDealIds)
	const [view, setView] = useState('board')
	const [filters, setFilters] = useState<DealFilters>(DEFAULT_FILTERS)
	// drag-preview only — reset on drop
	const [dragOverrides, setDragOverrides] = useState<Record<string, DealStage>>({})
	const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null)
	const [dragWidth, setDragWidth] = useState<number | null>(null)
	const [createDialogOpen, setCreateDialogOpen] = useState(false)
	// The deal being closed is kept after the dialog closes so the dialog's
	// post-success warnings flow can finish before the preset goes away.
	const [closingDeal,     setClosingDeal]     = useState<EnrichedDeal | null>(null)
	const [closeDialogOpen, setCloseDialogOpen] = useState(false)

	const sensors = useSensors(
		useSensor(MouseSensor, { activationConstraint: { distance: 5 } }),
		useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } }),
	)

	const { data: apiData, isLoading } = useListDealsQuery({ status: 'ACTIVE', limit: 100 })
	const [updateStage] = useUpdateDealStageMutation()

	// Deals only carry the property's address, so two properties at one address
	// are indistinguishable in the filter. Type and status come from here.
	const { data: propertiesData } = useListPropertiesQuery({ limit: 200 })
	// Ближайшая назначенная встреча по каждой сделке — нижняя строка карточки.
	// Отменённые и прошедшие не в счёт: доска отвечает на «что дальше».
	const { data: meetingsData } = useListMeetingsQuery()
	const nextEvents = useMemo(() => {
		const now = new Date().toISOString()
		const byDeal = new Map<string, string>()
		for (const m of meetingsData?.items ?? []) {
			if (m.status === 'CANCELLED' || m.status === 'COMPLETED') continue
			if (m.scheduledAt < now) continue
			const current = byDeal.get(m.dealId)
			if (!current || m.scheduledAt < current) byDeal.set(m.dealId, m.scheduledAt)
		}
		return new Map([...byDeal].map(([dealId, iso]) => [dealId, formatNextEvent(iso, t)]))
	}, [meetingsData, t])
	const propertyFacts = useMemo(
		() => new Map((propertiesData?.properties ?? []).map((p) => [p.id, { type: p.type, status: p.status }])),
		[propertiesData]
	)

	// Deal.propertyTitle / propertyImage / propertyAddress / propertyPrice / clientPhone / clientEmail
	// должны приходить заполненными от бека (см. project_dto_gaps_pending.md →
	// "Rental + Deal denormalization for list views"). Фронт ничего не дособирает —
	// показывает что есть; пустые fallbacks ниже сигнализируют о бек-гэпе.
	const deals = useMemo<EnrichedDeal[]>(() => {
		const apiDeals = apiData?.items ?? []
		return apiDeals
			.filter((d) => d.status === 'ACTIVE')
			.filter((d) => !storeHidden.includes(d.id))
			.map<EnrichedDeal>((d) => ({
				...d,
				stage: dragOverrides[d.id] ?? storeOverrides[d.id] ?? d.stage,
			}))
	}, [apiData, dragOverrides, storeOverrides, storeHidden])

	const filtered = useMemo(() => applyFilters(deals, filters), [deals, filters])

	// Unique values for filter dropdowns — derived from loaded deals
	const filterOptions = useMemo(() => {
		const properties = new Map<string, string>()
		const clients    = new Map<string, string>()
		const sources    = new Set<string>()
		for (const d of deals) {
			if (d.propertyId && d.propertyTitle) properties.set(d.propertyId, d.propertyTitle)
			if (d.contactId  && d.clientName)    clients.set(d.contactId, d.clientName)
			if (d.source) sources.add(d.source)
		}
		return { properties: disambiguatePropertyLabels(properties, propertyFacts, t), clients, sources: [...sources] }
	}, [deals, propertyFacts, t])

	const activeDeal = useMemo(
		() => (activeId ? deals.find((d) => d.id === String(activeId)) : undefined),
		[activeId, deals]
	)

	const stageCounts = useMemo(() => {
		const counts = Object.fromEntries(ACTIVE_STAGES.map((s) => [s, 0])) as Record<DealStage, number>
		for (const d of filtered) counts[d.stage]++
		return counts
	}, [filtered])

	const dealsByStage = useMemo(() => {
		const grouped = Object.fromEntries(ACTIVE_STAGES.map((s) => [s, [] as EnrichedDeal[]])) as Record<DealStage, EnrichedDeal[]>
		for (const d of filtered) grouped[d.stage]?.push(d)
		return grouped
	}, [filtered])

	const handleDragStart = useCallback((e: DragStartEvent) => {
		setActiveId(e.active.id)
		const node = e.active.rect.current.initial
		if (node) setDragWidth(node.width)
	}, [])

	const handleDragEnd = useCallback((event: DragEndEvent) => {
		const { active, over } = event
		setActiveId(null)
		setDragWidth(null)
		setDragOverrides({})
		if (!over || active.id === over.id) return

		const overId = String(over.id)
		const dealId = String(active.id)

		if (overId === 'action-close') {
			// Opens StartRentalDialog preseeded from the deal. The /rent/start
			// orchestrator closes the deal server-side via dealId — no blind
			// updateStage/createRental fire-and-forget here anymore.
			const deal = deals.find((d) => d.id === dealId)
			if (deal) {
				setClosingDeal(deal)
				setCloseDialogOpen(true)
			}
			return
		}
		if (overId === 'action-cancel') {
			dispatch(hideDeal(dealId))
			// crm-service auto-sets status=CANCELLED via STAGE_TERMINAL_STATUS when stage=CANCELLED
			updateStage({ id: dealId, body: { stage: 'CANCELLED' as DealStage } })
			return
		}

		// Resolve source stage: store override > API base
		const originalDeals = apiData?.items ?? []
		const baseStage = originalDeals.find((d) => d.id === dealId)?.stage
		const originalStage = storeOverrides[dealId] ?? baseStage
		const overStage = overId.startsWith('column-')
			? overId.replace('column-', '') as DealStage
			: originalDeals.find((d) => d.id === overId)?.stage

		if (originalStage !== overStage && overStage) {
			dispatch(setDealStage({ id: dealId, stage: overStage }))
			updateStage({ id: dealId, body: { stage: overStage } })
				.unwrap()
				.catch((err) => console.error('[DragEnd] updateStage FAILED', err))
		}
	}, [apiData, updateStage, deals, dispatch, storeOverrides])

	const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
		setFilters((prev) => ({ ...prev, search: e.target.value }))
	}, [])

	return (
		<div className="flex h-full flex-col">
			<div className="flex items-center justify-between px-4 py-3">
				<div className="flex items-center gap-2">
					<Tabs value={view} onValueChange={setView}>
						<TabsList variant="pills">
							<TabsTrigger variant="pills" value="board">{t('deals.viewBoard')}</TabsTrigger>
							<TabsTrigger variant="pills" value="list">{t('deals.viewList')}</TabsTrigger>
						</TabsList>
					</Tabs>
					<Select
						value={filters.property || 'all'}
						onValueChange={(v) => setFilters((p) => ({ ...p, property: v === 'all' ? '' : v }))}
					>
						<SelectTrigger width="lg" tone="muted">
							<SelectValue placeholder={t('deals.filterProperty')} />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value="all">{t('deals.allProperties')}</SelectItem>
							{[...filterOptions.properties.entries()].map(([id, title]) => (
								<SelectItem key={id} value={id}>{title}</SelectItem>
							))}
						</SelectContent>
					</Select>
					<Select
						value={filters.client || 'all'}
						onValueChange={(v) => setFilters((p) => ({ ...p, client: v === 'all' ? '' : v }))}
					>
						<SelectTrigger width="lg" tone="muted">
							<SelectValue placeholder={t('deals.filterClient')} />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value="all">{t('deals.allClients')}</SelectItem>
							{[...filterOptions.clients.entries()].map(([id, name]) => (
								<SelectItem key={id} value={id}>{name}</SelectItem>
							))}
						</SelectContent>
					</Select>
					<Select
						value={filters.source || 'all'}
						onValueChange={(v) => setFilters((p) => ({ ...p, source: v === 'all' ? '' : v }))}
					>
						<SelectTrigger width="md" tone="muted">
							<SelectValue placeholder={t('deals.completed.filterSource')} />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value="all">{t('deals.completed.allSources')}</SelectItem>
							{filterOptions.sources.map((s) => (
								<SelectItem key={s} value={s}>{s}</SelectItem>
							))}
						</SelectContent>
					</Select>
				</div>
				<div className="flex items-center gap-2">
					<SearchInput
						placeholder={t('deals.search')}
						value={filters.search}
						className="w-60"
						onChange={handleSearchChange}
					/>
					{canCreate && (
						<Button size="sm" onClick={() => setCreateDialogOpen(true)}>{t('deals.newDeal')}</Button>
					)}
				</div>
			</div>

			{isLoading && deals.length === 0 ? (
				<div className="flex flex-1 items-center justify-center">
					<Spinner />
				</div>
			) : filtered.length === 0 && view === 'list' ? (
				<div className="flex flex-1 items-center justify-center">
					<p className="text-sm text-(--color-muted-foreground)">{t('deals.emptyActive')}</p>
				</div>
			) : view === 'list' ? (
				<DealsListView deals={filtered} />
			) : (
				<DndContext
					sensors={sensors}
					collisionDetection={(args) => {
						const pointerCollisions = pointerWithin(args)
						return pointerCollisions.length > 0 ? pointerCollisions : rectIntersection(args)
					}}
					onDragStart={handleDragStart}
					onDragEnd={handleDragEnd}
				>
					<div className="flex flex-1 gap-5 overflow-x-auto overflow-y-auto px-4 pb-4">
						{ACTIVE_STAGES.map((stage) => (
							<KanbanColumn
								key={stage}
								stage={stage}
								label={t(DEAL_STAGE_KEYS[stage])}
								count={stageCounts[stage]}
								deals={dealsByStage[stage]}
								nextEvents={nextEvents}
							/>
						))}
					</div>

					{activeId && (
						<div className="pointer-events-none fixed inset-x-0 bottom-6 z-50 p-4">
							<div className="pointer-events-auto ml-auto flex w-fit overflow-hidden rounded-(--radius-lg) shadow-(--shadow-lg)">
								{canStartRental && (
									<DragActionZone id="action-close" label={t('deals.actionClose')} variant="success" />
								)}
								<DragActionZone id="action-cancel" label={t('deals.actionCancel')} variant="destructive" />
							</div>
						</div>
					)}

					{/* Та же карточка, что под курсором, но без ссылок:
					    перетаскиваемая копия ничего не открывает. */}
					<DragOverlay dropAnimation={null}>
						{activeDeal && (
							<DealKanbanCard
								style={dragWidth ? { width: dragWidth } : undefined}
								className="rotate-2 shadow-lg"
								address={activeDeal.propertyTitle ?? activeDeal.title ?? activeDeal.id}
								price={formatMonthlyPriceWithPeriod(activeDeal)}
								date={formatDate(activeDeal.createdAt)}
								source={renderBadge(activeDeal)}
								clientName={activeDeal.clientName}
								clientPhone={formatPhone(activeDeal.clientPhone) || undefined}
								manager={renderManager(activeDeal)}
								messages={activeDeal.messageCount ? <MessageCounter count={activeDeal.messageCount} /> : undefined}
							/>
						)}
					</DragOverlay>
				</DndContext>
			)}

			<CreateDealDialog open={createDialogOpen} onOpenChange={setCreateDialogOpen} />

			{/* Stays mounted after closing so the post-success warnings dialog
			    can render. Cancelling just closes — the deal stays on the board. */}
			<StartRentalDialog
				open={closeDialogOpen}
				onOpenChange={setCloseDialogOpen}
				preset={closingDeal ? {
					contactId:  closingDeal.contactId,
					propertyId: closingDeal.propertyId,
					dealId:     closingDeal.id,
					rentAmount: closingDeal.amount ?? undefined,
				} : undefined}
			/>
		</div>
	)
}
